import { ethers } from "ethers";
import { readContract, writeContract, getBalance, simulateContract, sendTransaction, getAccount } from "@wagmi/core";
import { config } from "../config/appkit";
import { bsc, bscTestnet } from "@reown/appkit/networks";
import { CONTRACT_ABI, ERC20_ABI, FUNDRAISING_CONTRACT_ABI } from "./contractABI";

export class AppKitBlockchainService {
  private contractAddress: string;
  private dxfTokenAddress: string;
  private fundraisingContractAddress: string;
  public isTestnet: boolean;

  constructor() {
    this.contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS!;
    this.dxfTokenAddress = process.env.REACT_APP_DXF_TOKEN_ADDRESS!;
    this.fundraisingContractAddress = process.env.REACT_APP_FUNDRAISING_CONTRACT_ADDRESS!;
    this.isTestnet = process.env.REACT_APP_USE_TESTNET === "true";
  }

  getExpectedChainId(): number {
    return this.isTestnet ? bscTestnet.id : bsc.id;
  }

  getExpectedNetworkName(): string {
    return this.isTestnet ? "BSC Testnet" : "BSC Mainnet";
  }

  async getBalance(address: string): Promise<string> {
    try {
      const balance = await getBalance(config, {
        address: address as `0x${string}`,
        chainId: this.getExpectedChainId(),
      });

      return ethers.formatEther(balance.value);
    } catch (error) {
      console.error("Error getting BNB balance:", error);
      return "0";
    }
  }

  private cachedDecimals: number | null = null;

  async getDXFBalance(address: string): Promise<string> {
    try {
      if (!this.dxfTokenAddress) {
        console.warn("DXF token address not configured");
        return "0";
      }

      const balance = await readContract(config, {
        address: this.dxfTokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "balanceOf",
        args: [address as `0x${string}`],
        chainId: this.getExpectedChainId(),
      });

      // Cache decimals to avoid repeated calls
      if (this.cachedDecimals === null) {
        const decimals = await readContract(config, {
          address: this.dxfTokenAddress as `0x${string}`,
          abi: ERC20_ABI,
          functionName: "decimals",
          chainId: this.getExpectedChainId(),
        });
        this.cachedDecimals = decimals as number;
      }

      // Format balance using cached decimals
      const formattedBalance = ethers.formatUnits(
        balance as bigint,
        this.cachedDecimals,
      );
      return formattedBalance;
    } catch (error) {
      console.error("Error getting DXF balance:", error);
      return "0";
    }
  }

  private async estimateGasLimit(
    contractAddress: string,
    abi: any,
    functionName: string,
    args: any[],
    value?: bigint
  ): Promise<bigint> {
    try {
      // Use simulateContract to get gas estimate
      const simulation = await simulateContract(config, {
        address: contractAddress as `0x${string}`,
        abi,
        functionName,
        args,
        value,
        chainId: this.getExpectedChainId(),
      });

      // Extract gas estimate from simulation request
      const estimatedGas = simulation.request.gas || BigInt(500000); // Fallback gas limit

      // Add 20% buffer to estimated gas
      const gasWithBuffer = (estimatedGas * BigInt(120)) / BigInt(100);
      
      console.log(`Gas estimation for ${functionName}:`, {
        estimated: estimatedGas.toString(),
        withBuffer: gasWithBuffer.toString(),
        bufferAdded: (gasWithBuffer - estimatedGas).toString()
      });

      return gasWithBuffer;
    } catch (error) {
      console.error(`Gas estimation failed for ${functionName}:`, error);
      throw new Error(`Failed to estimate gas for ${functionName}. The transaction may fail.`);
    }
  }

  async getCurrentRewardRatio(): Promise<string> {
    try {
      if (!this.contractAddress) {
        return "10000"; // Default fallback
      }

      const ratio = await readContract(config, {
        address: this.contractAddress as `0x${string}`,
        abi: CONTRACT_ABI,
        functionName: "getCurrentRewardRatio",
        chainId: this.getExpectedChainId(),
      });

      return ethers.formatEther(ratio as bigint);
    } catch (error) {
      console.error("Error getting current reward ratio:", error);
      return "10000"; // Default fallback
    }
  }

  async createGameOnChain(
    preliminaryGameId: string,
    gameSeedHash: string,
    algoVersion: string,
    gameConfig: string,
    deadline: number,
    signature: string,
    betAmount: string,
    dxfUtilityAmount: string = "0",
  ): Promise<string> {
    try {
      if (!this.contractAddress) {
        throw new Error("Contract address not configured");
      }

      console.log("Creating game on chain with params:", {
        preliminaryGameId,
        gameSeedHash: gameSeedHash.substring(0, 10) + "...",
        algoVersion,
        gameConfig: gameConfig.substring(0, 50) + "...",
        deadline,
        betAmount,
        dxfUtilityAmount,
        chainId: this.getExpectedChainId(),
      });

      // Create struct parameter to match smart contract
      const createGameParams = {
        preliminaryGameId,
        gameSeedHash: gameSeedHash as `0x${string}`,
        algoVersion,
        gameConfig,
        dxfUtilityAmount: BigInt(dxfUtilityAmount),
        deadline: BigInt(deadline),
        serverSignature: signature as `0x${string}`,
      };

      // Estimate gas before transaction
      const gasLimit = await this.estimateGasLimit(
        this.contractAddress,
        CONTRACT_ABI,
        "createGame",
        [createGameParams],
        BigInt(betAmount)
      );

      const hash = await writeContract(config, {
        address: this.contractAddress as `0x${string}`,
        abi: CONTRACT_ABI,
        functionName: "createGame",
        args: [createGameParams],
        value: BigInt(betAmount),
        gas: gasLimit,
        chainId: this.getExpectedChainId(),
      });

      console.log("Transaction sent successfully:", hash);
      return hash;
    } catch (error) {
      console.error("Error creating game on chain:", error);

      // Enhanced error handling for better debugging
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();

        // Check for various rejection patterns
        if (
          errorMessage.includes("user rejected") ||
          errorMessage.includes("user denied") ||
          errorMessage.includes("rejected")
        ) {
          console.warn(
            "Transaction appears to be rejected, but this might be a false positive",
          );
          console.log("Full error details:", {
            message: error.message,
            stack: error.stack,
            name: error.name,
          });

          // Provide more specific error message
          throw new Error(
            "Transaction was not completed. This could be due to user rejection or a wallet connection issue. Please try again.",
          );
        } else if (
          errorMessage.includes("insufficient funds") ||
          errorMessage.includes("insufficient balance")
        ) {
          throw new Error("Insufficient balance for transaction");
        } else if (
          errorMessage.includes("network") ||
          errorMessage.includes("connection") ||
          errorMessage.includes("provider")
        ) {
          throw new Error(
            "Network connection issue. Please check your wallet connection and try again.",
          );
        } else if (
          errorMessage.includes("gas") ||
          errorMessage.includes("fee")
        ) {
          throw new Error(
            "Gas estimation failed. The transaction might fail or network fees are too high.",
          );
        }
      }

      // Log full error details for debugging
      console.error("Detailed error information:", {
        error: error,
        type: typeof error,
        constructor: error?.constructor?.name,
        keys: Object.keys(error || {}),
      });

      throw new Error(
        "Failed to create game on blockchain. Please try again or contact support if the issue persists.",
      );
    }
  }

  async cashOutOnChain(cashOutData: any): Promise<string> {
    try {
      if (!this.contractAddress) {
        throw new Error("Contract address not configured");
      }

      console.log("Cashing out on chain with params:", {
        onChainGameId: cashOutData.onChainGameId,
        payoutAmount: cashOutData.payoutAmount,
        deadline: cashOutData.deadline,
        chainId: this.getExpectedChainId(),
      });

      // Estimate gas before transaction
      const gasLimit = await this.estimateGasLimit(
        this.contractAddress,
        CONTRACT_ABI,
        "cashOut",
        [
          BigInt(cashOutData.onChainGameId),
          BigInt(cashOutData.payoutAmount),
          cashOutData.gameState,
          cashOutData.gameSeed,
          BigInt(cashOutData.deadline),
          cashOutData.signature as `0x${string}`,
        ]
      );

      const hash = await writeContract(config, {
        address: this.contractAddress as `0x${string}`,
        abi: CONTRACT_ABI,
        functionName: "cashOut",
        args: [
          BigInt(cashOutData.onChainGameId),
          BigInt(cashOutData.payoutAmount),
          cashOutData.gameState,
          cashOutData.gameSeed,
          BigInt(cashOutData.deadline),
          cashOutData.signature as `0x${string}`,
        ],
        gas: gasLimit,
        chainId: this.getExpectedChainId(),
      });

      console.log("Cash out transaction sent successfully:", hash);
      return hash;
    } catch (error) {
      console.error("Error cashing out on chain:", error);

      // Enhanced error handling for better debugging
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();

        // Check for various rejection patterns
        if (
          errorMessage.includes("user rejected") ||
          errorMessage.includes("user denied") ||
          errorMessage.includes("rejected")
        ) {
          console.warn(
            "Cash out transaction appears to be rejected, but this might be a false positive",
          );
          console.log("Full error details:", {
            message: error.message,
            stack: error.stack,
            name: error.name,
          });

          // Provide more specific error message
          throw new Error(
            "Cash out was not completed. This could be due to user rejection or a wallet connection issue. Please try again.",
          );
        } else if (
          errorMessage.includes("insufficient funds") ||
          errorMessage.includes("insufficient balance")
        ) {
          throw new Error("Insufficient balance for transaction");
        } else if (
          errorMessage.includes("network") ||
          errorMessage.includes("connection") ||
          errorMessage.includes("provider")
        ) {
          throw new Error(
            "Network connection issue. Please check your wallet connection and try again.",
          );
        } else if (
          errorMessage.includes("gas") ||
          errorMessage.includes("fee")
        ) {
          throw new Error(
            "Gas estimation failed. The transaction might fail or network fees are too high.",
          );
        }
      }

      // Log full error details for debugging
      console.error("Detailed cash out error information:", {
        error: error,
        type: typeof error,
        constructor: error?.constructor?.name,
        keys: Object.keys(error || {}),
      });

      throw new Error(
        "Failed to cash out on blockchain. Please try again or contact support if the issue persists.",
      );
    }
  }

  async cashOutAndCreateGameOnChain(
    cashOutData: any,
    newGameData: {
      gameId: string;
      gameSeedHash: string;
      algoVersion: string;
      gameConfig: string;
      deadline: number;
      signature: string;
      betAmount: string;
      dxfUtilityAmount: string;
    }
  ): Promise<string> {
    try {
      if (!this.contractAddress) {
        throw new Error("Contract address not configured");
      }

      console.log("Cashing out and creating new game on chain with params:", {
        onChainGameId: cashOutData.onChainGameId,
        payoutAmount: cashOutData.payoutAmount,
        deadline: cashOutData.deadline,
        newGameId: newGameData.gameId,
        betAmount: newGameData.betAmount,
        chainId: this.getExpectedChainId(),
      });

      // Prepare parameters according to ABI structure (single struct)
      const params = {
        onChainGameId: BigInt(cashOutData.onChainGameId),
        payoutAmount: BigInt(cashOutData.payoutAmount),
        gameState: cashOutData.gameState,
        gameSeed: cashOutData.gameSeed,
        cashOutDeadline: BigInt(cashOutData.deadline),
        cashOutServerSignature: cashOutData.signature as `0x${string}`,
        newGameParams: {
          preliminaryGameId: newGameData.gameId,
          gameSeedHash: newGameData.gameSeedHash as `0x${string}`,
          algoVersion: newGameData.algoVersion,
          gameConfig: newGameData.gameConfig,
          dxfUtilityAmount: BigInt(newGameData.dxfUtilityAmount),
          deadline: BigInt(newGameData.deadline),
          serverSignature: newGameData.signature as `0x${string}`,
        },
      };

      // Estimate gas before transaction
      const gasLimit = await this.estimateGasLimit(
        this.contractAddress,
        CONTRACT_ABI,
        "cashOutAndCreateGame",
        [params]
      );

      const hash = await writeContract(config, {
        address: this.contractAddress as `0x${string}`,
        abi: CONTRACT_ABI,
        functionName: "cashOutAndCreateGame",
        args: [params],
        gas: gasLimit,
        chainId: this.getExpectedChainId(),
      });

      console.log("Cash out and create game transaction sent successfully:", hash);
      return hash;
    } catch (error) {
      console.error("Error cashing out and creating game on chain:", error);

      // Enhanced error handling for better debugging
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();

        // Check for various rejection patterns
        if (
          errorMessage.includes("user rejected") ||
          errorMessage.includes("user denied") ||
          errorMessage.includes("rejected")
        ) {
          console.warn(
            "Cash out and create game transaction appears to be rejected, but this might be a false positive"
          );
          console.log("Full error details:", {
            message: error.message,
            stack: error.stack,
            name: error.name,
          });

          // Provide more specific error message
          throw new Error(
            "Cash out and create game was not completed. This could be due to user rejection or a wallet connection issue. Please try again."
          );
        } else if (
          errorMessage.includes("insufficient funds") ||
          errorMessage.includes("insufficient balance")
        ) {
          throw new Error("Insufficient balance for transaction");
        }
      }

      throw new Error(
        `Cash out and create game failed: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  async getOnChainGameId(preliminaryGameId: string): Promise<string> {
    try {
      if (!this.contractAddress) {
        throw new Error("Contract address not configured");
      }

      const onChainId = await readContract(config, {
        address: this.contractAddress as `0x${string}`,
        abi: CONTRACT_ABI,
        functionName: "getOnChainGameId",
        args: [preliminaryGameId],
        chainId: this.getExpectedChainId(),
      });

      return (onChainId as bigint).toString();
    } catch (error) {
      console.error("Error getting on-chain game ID:", error);
      throw new Error("Failed to get on-chain game ID");
    }
  }

  async getGameDetails(gameId: string) {
    try {
      if (!this.contractAddress) {
        throw new Error("Contract address not configured");
      }

      const details = await readContract(config, {
        address: this.contractAddress as `0x${string}`,
        abi: CONTRACT_ABI,
        functionName: "getGameDetailsWithPreliminaryId",
        args: [gameId],
        chainId: this.getExpectedChainId(),
      });

      // With proper ABI, details is now a structured object
      const gameDetails = details as {
        createdAt: bigint;
        player: string;
        betAmount: bigint;
        status: number;
        payoutAmount: bigint;
        gameSeedHash: string;
        gameSeed: string;
        algoVersion: string;
        gameConfig: string;
        gameState: string;
      };

      return {
        createdAt: gameDetails.createdAt,
        player: gameDetails.player,
        betAmount: ethers.formatEther(gameDetails.betAmount),
        status: gameDetails.status,
        payoutAmount: ethers.formatEther(gameDetails.payoutAmount),
        gameSeedHash: gameDetails.gameSeedHash,
        gameSeed: gameDetails.gameSeed,
        algoVersion: gameDetails.algoVersion,
        gameConfig: gameDetails.gameConfig,
        gameState: gameDetails.gameState,
      };
    } catch (error) {
      console.error("Error getting game details:", error);
      throw new Error("Failed to get game details");
    }
  }

  /**
   * Get DXF token allowance for the DeathFun contract
   */
  async getDXFAllowance(ownerAddress: string): Promise<string> {
    try {
      if (!this.dxfTokenAddress || !this.contractAddress) {
        console.warn("DXF token or contract address not configured");
        return "0";
      }

      const allowance = await readContract(config, {
        address: this.dxfTokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [ownerAddress as `0x${string}`, this.contractAddress as `0x${string}`],
        chainId: this.getExpectedChainId(),
      });

      // DXF tokens use 18 decimals
      return ethers.formatUnits(allowance as bigint, 18);
    } catch (error) {
      console.error("Error getting DXF allowance:", error);
      return "0";
    }
  }

  /**
   * Approve DXF tokens for the DeathFun contract
   */
  async approveDXF(amount: string): Promise<string> {
    try {
      if (!this.dxfTokenAddress || !this.contractAddress) {
        throw new Error("DXF token or contract address not configured");
      }

      // DXF tokens use 18 decimals
      const amountInWei = ethers.parseUnits(amount, 18);

      console.log("Approving DXF tokens:", {
        amount,
        amountInWei: amountInWei.toString(),
        spender: this.contractAddress,
      });

      // Estimate gas before transaction
      const gasLimit = await this.estimateGasLimit(
        this.dxfTokenAddress,
        ERC20_ABI,
        "approve",
        [this.contractAddress as `0x${string}`, amountInWei]
      );

      const hash = await writeContract(config, {
        address: this.dxfTokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "approve",
        args: [this.contractAddress as `0x${string}`, amountInWei],
        gas: gasLimit,
        chainId: this.getExpectedChainId(),
      });

      return hash;
    } catch (error) {
      console.error("Error approving DXF tokens:", error);
      throw error;
    }
  }

  /**
   * Check if user has sufficient DXF allowance for the required amount
   */
  async hasSufficientDXFAllowance(ownerAddress: string, requiredAmount: string): Promise<boolean> {
    try {
      const allowance = await this.getDXFAllowance(ownerAddress);
      const allowanceBigInt = ethers.parseUnits(allowance, 18);
      const requiredBigInt = ethers.parseUnits(requiredAmount, 18);
      
      return allowanceBigInt >= requiredBigInt;
    } catch (error) {
      console.error("Error checking DXF allowance:", error);
      return false;
    }
  }

  /**
   * Send BNB to the contract for streak recovery (native transfer)
   * @param amount Amount of BNB to send as string (e.g., "0.005")
   * @returns Transaction hash
   */
  async sendBNBToContract(amount: string): Promise<string> {
    try {
      console.log(`Sending ${amount} BNB to contract for streak recovery...`);
      
      // Convert BNB amount to wei
      const amountInWei = ethers.parseEther(amount);
      
      // Get current account to send from
      const account = getAccount(config);
      
      if (!account.address) {
        throw new Error("No wallet connected");
      }

      // Send native BNB transfer to contract address
      const hash = await sendTransaction(config, {
        to: this.contractAddress as `0x${string}`,
        value: amountInWei,
        chainId: this.getExpectedChainId(),
      });

      console.log("BNB transfer transaction sent successfully:", hash);
      
      // Wait for transaction confirmation
      console.log("Waiting for transaction confirmation...");
      const receipt = await this.waitForTransaction(hash);
      
      if (!receipt.status) {
        throw new Error("Transaction failed");
      }
      
      console.log("Transaction confirmed successfully");
      return hash;
    } catch (error) {
      console.error("Error sending BNB to contract:", error);

      // Enhanced error handling
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();

        if (
          errorMessage.includes("user rejected") ||
          errorMessage.includes("user denied") ||
          errorMessage.includes("user cancelled")
        ) {
          throw new Error("Transaction was cancelled by user");
        } else if (errorMessage.includes("insufficient funds")) {
          throw new Error("Insufficient BNB balance");
        } else if (errorMessage.includes("gas")) {
          throw new Error("Transaction failed due to gas issues");
        }
      }

      throw error;
    }
  }

  /**
   * Wait for transaction to be confirmed
   * @param hash Transaction hash
   * @returns Transaction receipt
   */
  private async waitForTransaction(hash: string): Promise<any> {
    const provider = new ethers.JsonRpcProvider(
      this.isTestnet 
        ? "https://data-seed-prebsc-1-s1.binance.org:8545/"
        : "https://bsc-dataseed.binance.org/"
    );
    
    // Wait for transaction with timeout
    const receipt = await provider.waitForTransaction(hash, 1, 30000); // 30 second timeout
    
    if (!receipt) {
      throw new Error("Transaction confirmation timeout");
    }
    
    return receipt;
  }

  // ===============================================
  // FundRaising Contract Methods
  // ===============================================

  /**
   * Get all agreement IDs for a specific investor
   */
  async getAgreementsByInvestor(investorAddress: string): Promise<bigint[]> {
    try {
      if (!this.fundraisingContractAddress) {
        throw new Error("FundRaising contract address not configured");
      }

      const agreementIds = await readContract(config, {
        address: this.fundraisingContractAddress as `0x${string}`,
        abi: FUNDRAISING_CONTRACT_ABI,
        functionName: "getAgreementsByInvestor",
        args: [investorAddress as `0x${string}`],
        chainId: this.getExpectedChainId(),
      });

      return agreementIds as bigint[];
    } catch (error) {
      console.error("Error getting agreements by investor:", error);
      throw error;
    }
  }

  /**
   * Get agreement details by ID
   */
  async getAgreement(agreementId: bigint): Promise<{
    investor: string;
    investTokenAddr: string;
    investTokenAmount: bigint;
    dxfTokenAmount: bigint;
    expiresAt: bigint;
    status: number;
  }> {
    try {
      if (!this.fundraisingContractAddress) {
        throw new Error("FundRaising contract address not configured");
      }

      const agreement = await readContract(config, {
        address: this.fundraisingContractAddress as `0x${string}`,
        abi: FUNDRAISING_CONTRACT_ABI,
        functionName: "getAgreement",
        args: [agreementId],
        chainId: this.getExpectedChainId(),
      });

      return agreement as {
        investor: string;
        investTokenAddr: string;
        investTokenAmount: bigint;
        dxfTokenAmount: bigint;
        expiresAt: bigint;
        status: number;
      };
    } catch (error) {
      console.error("Error getting agreement:", error);
      throw error;
    }
  }

  /**
   * Invest in an agreement (for native BNB)
   */
  async investInAgreement(agreementId: bigint, value?: bigint): Promise<string> {
    try {
      if (!this.fundraisingContractAddress) {
        throw new Error("FundRaising contract address not configured");
      }

      console.log("Investing in agreement:", {
        agreementId: agreementId.toString(),
        value: value ? ethers.formatEther(value) : "0",
      });

      // Estimate gas before transaction
      const gasLimit = await this.estimateGasLimit(
        this.fundraisingContractAddress,
        FUNDRAISING_CONTRACT_ABI,
        "invest",
        [agreementId],
        value
      );

      const hash = await writeContract(config, {
        address: this.fundraisingContractAddress as `0x${string}`,
        abi: FUNDRAISING_CONTRACT_ABI,
        functionName: "invest",
        args: [agreementId],
        value: value,
        gas: gasLimit,
        chainId: this.getExpectedChainId(),
      });

      return hash;
    } catch (error) {
      console.error("Error investing in agreement:", error);
      throw error;
    }
  }

  /**
   * Get ERC20 token balance for a specific address
   */
  async getERC20Balance(tokenAddress: string, userAddress: string): Promise<string> {
    try {
      const balance = await readContract(config, {
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "balanceOf",
        args: [userAddress as `0x${string}`],
        chainId: this.getExpectedChainId(),
      });

      // Get decimals
      const decimals = await readContract(config, {
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "decimals",
        chainId: this.getExpectedChainId(),
      });

      return ethers.formatUnits(balance as bigint, decimals as number);
    } catch (error) {
      console.error("Error getting ERC20 balance:", error);
      return "0";
    }
  }

  /**
   * Get ERC20 token allowance for FundRaising contract
   */
  async getERC20AllowanceForFundRaising(
    tokenAddress: string,
    ownerAddress: string
  ): Promise<string> {
    try {
      if (!this.fundraisingContractAddress) {
        console.warn("FundRaising contract address not configured");
        return "0";
      }

      const allowance = await readContract(config, {
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [
          ownerAddress as `0x${string}`,
          this.fundraisingContractAddress as `0x${string}`
        ],
        chainId: this.getExpectedChainId(),
      });

      // Get decimals
      const decimals = await readContract(config, {
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "decimals",
        chainId: this.getExpectedChainId(),
      });

      return ethers.formatUnits(allowance as bigint, decimals as number);
    } catch (error) {
      console.error("Error getting ERC20 allowance:", error);
      return "0";
    }
  }

  /**
   * Approve ERC20 tokens for FundRaising contract
   */
  async approveERC20ForFundRaising(
    tokenAddress: string,
    amount: string,
    decimals: number = 18
  ): Promise<string> {
    try {
      if (!this.fundraisingContractAddress) {
        throw new Error("FundRaising contract address not configured");
      }

      const amountInWei = ethers.parseUnits(amount, decimals);

      console.log("Approving ERC20 tokens:", {
        tokenAddress,
        amount,
        amountInWei: amountInWei.toString(),
        spender: this.fundraisingContractAddress,
      });

      // Estimate gas before transaction
      const gasLimit = await this.estimateGasLimit(
        tokenAddress,
        ERC20_ABI,
        "approve",
        [this.fundraisingContractAddress as `0x${string}`, amountInWei]
      );

      const hash = await writeContract(config, {
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "approve",
        args: [this.fundraisingContractAddress as `0x${string}`, amountInWei],
        gas: gasLimit,
        chainId: this.getExpectedChainId(),
      });

      return hash;
    } catch (error) {
      console.error("Error approving ERC20 tokens:", error);
      throw error;
    }
  }

  /**
   * Get ERC20 token symbol
   */
  async getERC20Symbol(tokenAddress: string): Promise<string> {
    try {
      const symbol = await readContract(config, {
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "symbol",
        chainId: this.getExpectedChainId(),
      });

      return symbol as string;
    } catch (error) {
      console.error("Error getting ERC20 symbol:", error);
      return "TOKEN";
    }
  }

  /**
   * Get ERC20 token decimals
   */
  async getERC20Decimals(tokenAddress: string): Promise<number> {
    try {
      const decimals = await readContract(config, {
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "decimals",
        chainId: this.getExpectedChainId(),
      });

      return decimals as number;
    } catch (error) {
      console.error("Error getting ERC20 decimals:", error);
      return 18; // Default to 18 decimals
    }
  }

  // For compatibility with existing code
  isConnected(): boolean {
    // AppKit handles connection state, so we'll assume connected if addresses are configured
    return !!(this.contractAddress && this.dxfTokenAddress);
  }

  explorerTx(txHash: string | undefined): string {
    if (!txHash) return "";
    const baseUrl = this.isTestnet ? "https://testnet.bscscan.com" : "https://bscscan.com";
    return `${baseUrl}/tx/${txHash}`;
  }
  
}

// Global instance
export const appKitBlockchainService = new AppKitBlockchainService();
